import { ID } from '@datorama/akita';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { AccountIssuer, AccountIssuerStatus } from './account-issuer.model';
import { AccountIssuersStore } from './account-issuers.store';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { Observable } from 'rxjs';
import { Cacheable } from 'ts-cacheable';

/**
 * Will only include get method since these are enum values in the data model and can't be modified.
 */
@Injectable({ providedIn: 'root' })
export class AccountIssuersService {
    constructor(
        private store: AccountIssuersStore,
        private http: HttpClient,
        private config: AppConfigQuery
    ) {}

    /** Clone category - create a new category by using an existing category as template.
     * @returns - observable for request
     */
    @Cacheable({
        maxAge: 1000
    })
    get() {
        return this.http
            .get<AccountIssuer[]>(`${this.config.get().adminBaseUrl}/v1/account-issuers`)
            .pipe(
                tap((entities) => {
                    this.store.set(entities);
                })
            );
    }

    /** Get single account issuer by id.
     * @param id - account issuer id
     * @returns - observable for request
     */
    getById(id: ID) {
        return this.http
            .get<AccountIssuer>(
                `${this.config.get().adminBaseUrl}/v1/account-issuers/${id}`
            )
            .pipe(
                tap((issuer) => {
                    this.store.upsert([issuer.accountIssuerId], issuer);
                })
            );
    }

    /** Create account issuer - create a new account issuer.
     * @param data - new account issuer data
     * @returns - observable for request
     */
    create(data: Partial<AccountIssuer>) {
        return this.http
            .post<AccountIssuer>(
                `${this.config.get().adminBaseUrl}/v1/account-issuers`,
                data
            )
            .pipe(
                tap((issuer) =>
                    this.store.add(issuer, {
                        prepend: true
                    })
                )
            );
    }

    /** Update - change account issuer details
     * @param data -
     * @param id -
     * @returns - observable for request
     */
    update(data: AccountIssuer, id: ID) {
        return this.http
            .put<AccountIssuer>(
                `${this.config.get().adminBaseUrl}/v1/account-issuers/${id}`,
                data
            )
            .pipe(tap((issuer) => this.store.upsert(issuer.accountIssuerId, issuer)));
    }

    upsert(data: AccountIssuer): Observable<AccountIssuer> {
        if (data.accountIssuerId) {
            return this.update(data, data.accountIssuerId);
        }
        return this.create(data);
    }

    /** Delete an account issuer from the system.
     * @param id - account issuer id
     * @returns - observable for request
     */
    delete(id: ID) {
        return this.http
            .delete<AccountIssuer>(
                `${this.config.get().adminBaseUrl}/v1/account-issuers/${id}`
            )
            .pipe(tap(() => this.store.remove(id)));
    }

    /** Disable/Enable (acts as a soft-delete) an account issuer from the system.
     * @param id - account issuer id
     * @param status - describes the wanted status to be set for the entitiy
     * @returns - observable for request
     */
    toggleEnabled(issuer: AccountIssuer) {
        return this.http
            .put<AccountIssuer>(
                `${this.config.get().adminBaseUrl}/v1/account-issuers/${
                    issuer.accountIssuerId
                }`,
                {
                    ...issuer,
                    status:
                        issuer.status === AccountIssuerStatus.ENABLED
                            ? AccountIssuerStatus.DISABLED
                            : AccountIssuerStatus.ENABLED
                }
            )
            .pipe(tap((i) => this.store.update(i.accountIssuerId, i)));
    }
}
